import { firestore } from "firebase";
import { useCallback, useEffect, useRef, useState } from "react";
import { deleteUndefined } from "./FirestoreHelper";
import { CollectionStore, FirestoreWhere, isValidFirestoreDocumentData, ValidData } from "./StoreHelper";
import { EntityKey, EntityValidData, useEntityStore } from "./EntityStoreHelper";
import { PerformanceTrace, PerformanceTraceAttributeAndMetric, useTracking } from "../Providers/TrackingProvider";

export interface LazyLoadCollectionStore<T> extends CollectionStore<T> {
    loadNextBatch: Function
}

const MAX_DOC_SIZE = 20;

export function useLazyCollectionStore<T>(props: {
    collection: firestore.CollectionReference<firestore.DocumentData>;
    is: (object: any) => object is T;
    sort: (a: T, b: T) => number;
    where?: FirestoreWhere[];
    order?: string | firestore.FieldPath,
    softDelete?: Partial<T>;
    batchCount?: number,
    loadAll?: boolean
    entityKey?: EntityKey<T>,
    traceName?: PerformanceTrace,
    traceMetric?: PerformanceTraceAttributeAndMetric
}): LazyLoadCollectionStore<T> {
    const {items, setItems, deleteItems} = useEntityStore<(T & EntityValidData<T>)>(props.entityKey);
    const {trackTrace} = useTracking();
    const traceRef = useRef(props.traceName ? trackTrace(props.traceName) : null);
    const [isLoading, setIsLoading] = useState(true);
    const [lastDocument, setLastDocument] = useState<firestore.DocumentData | null>(null);
    const [error, setError] = useState<Error>();
    const is = useCallback(props.is, []);
    const loadNextBatch = useRef<Function>(() => {
    });

    async function setFirebaseItem(item: T & (EntityValidData<T> | ValidData)) {
        await props.collection.doc(item.id).set(deleteUndefined(item));
    }

    async function updateFirebaseItem(item: Partial<T> & ValidData) {
        await props.collection.doc(item.id).update(deleteUndefined(item, true));
    }

    async function deleteFirebaseItem(item: T & ValidData) {
        if (props.softDelete) {
            await props.collection.doc(item.id).set({
                ...deleteUndefined(item),
                ...props.softDelete,
            });
        } else {
            await props.collection.doc(item.id).delete();
        }
    }

    const snapshotSuccessSubscriber = (collection: firestore.QuerySnapshot) => {
        const itemsToSet: Array<T & EntityValidData<T>> = [], itemsToDelete: Array<T & EntityValidData<T>> = [];

        collection.docChanges().forEach(change => {
            if (change.type === 'added' || change.type === 'modified') {
                const docData = change.doc.data();
                if (is(docData) && isValidFirestoreDocumentData(docData)) {
                    itemsToSet.push(docData);
                }
            }
            if (change.type === 'removed') {
                const docData = change.doc.data();
                if (is(docData) && isValidFirestoreDocumentData(docData)) {
                    itemsToDelete.push(docData);
                }
            }
        });

        setItems(itemsToSet);
        deleteItems(itemsToDelete);
        setError(undefined);
        setIsLoading(false);

        if (traceRef.current && props.traceMetric) {
            traceRef.current.putMetric(props.traceMetric, itemsToSet.length);
            traceRef.current.putAttribute(props.traceMetric, `${itemsToSet.length}`);
        }

        traceRef.current?.stop();
    }

    const snapshotErrorSubscriber = (error: Error) => {
        let path = `collection("${props.collection.path}")`;

        if (props.where?.length) {
            const whereStrings = props.where.map(
                (where) => `.where("${where[0]}", "${where[1]}", "${where[2]}")`
            );

            path += whereStrings.join("");
        }

        if (props.order) {
            path += `.orderBy("${props.order}")`
        }

        error.message = `${path} Error message: ${error.message}`;
        setError(error);
        setIsLoading(false);
    }


    useEffect(() => {
        let collectionRef:
            | firestore.CollectionReference<firestore.DocumentData>
            | firestore.Query<firestore.DocumentData> = props.collection;
        const subscribers: (() => void)[] = [];

        props.where?.forEach((where) => {
            collectionRef = collectionRef.where(...where);
        });

        if (props.order) {
            collectionRef = collectionRef.orderBy(props.order);
        }

        async function updateLastMessage(ref: firestore.Query): Promise<void> {
            if (!lastDocument) {
                return
            }
            const docs = (await ref.get()).docs;
            setLastDocument(docs.length === MAX_DOC_SIZE ? docs[0] : null);
        }

        function loadMessageBatch(batch?: {
            startAt?: firestore.DocumentData,
            endAt?: firestore.DocumentData,
            limit?: number
        }) {
            traceRef.current?.start();
            if (batch?.startAt) {
                return collectionRef
                    .startAt(batch.startAt)
                    .onSnapshot(
                        snapshotSuccessSubscriber,
                        snapshotErrorSubscriber
                    );
            }

            if (batch?.endAt) {
                const ref = collectionRef
                    .endBefore(batch.endAt)
                    .limitToLast(MAX_DOC_SIZE);

                updateLastMessage(ref);

                return ref.onSnapshot(
                    snapshotSuccessSubscriber,
                    snapshotErrorSubscriber
                );
            }

            return collectionRef.onSnapshot(
                snapshotSuccessSubscriber,
                snapshotErrorSubscriber
            );
        }

        loadNextBatch.current = () => {
            if (lastDocument?.exists) {
                subscribers.push(loadMessageBatch({
                    endAt: lastDocument,
                    limit: MAX_DOC_SIZE
                }));
            }
        }

        (async function () {
            const latestDocument = (await collectionRef.limitToLast(1).get()).docs[0];

            if (props.loadAll || !latestDocument.exists) {
                subscribers.push(loadMessageBatch());
                return;
            }

            if (latestDocument.exists) {
                subscribers.push(loadMessageBatch({
                    startAt: latestDocument
                }));
                subscribers.push(loadMessageBatch({
                    endAt: latestDocument,
                    limit: MAX_DOC_SIZE
                }));
            }
        })()


        return () => {
            subscribers.forEach(subscriber => subscriber());
        };
    }, [props.where, is, props.sort, props.collection, setItems, deleteItems]);

    return {
        items: items.sort(props.sort),
        isLoading,
        error,
        setItem: setFirebaseItem,
        updateItem: updateFirebaseItem,
        deleteItem: deleteFirebaseItem,
        loadNextBatch: loadNextBatch.current
    };
}
